<template>
  <div class="flex size-full flex-col overflow-hidden">
    <FullTillGridBreadcrumbs
      v-model:search="searchString"
    />
    <QueryLoading
      v-if="isProductsLoading || isCategoriesLoading"
    />
    <QueryError
      v-else-if="isProductsError || isCategoriesError"
    >
      Failed to load products
    </QueryError>
    <div
      v-else-if="
        filteredCategories.length > 0 ||
        filteredProducts?.length
      "
      class="overflow-y-auto p-4"
    >
      <div
        v-if="filteredCategories.length > 0"
        class="mb-4"
      >
        <h3
          class="mb-2 text-sm font-semibold uppercase text-background-fg/70"
        >
          Categories
        </h3>
        <div
          :class="[
            'grid',
            sparseModeEnabled
              ? 'grid-cols-2 gap-2'
              : 'grid-cols-3 gap-1',
          ]"
        >
          <template
            v-for="category in filteredCategories"
            :key="category.id"
          >
            <FullTillGridItemCategory
              :category="category"
              @click="handleClickCategory(category)"
            />
          </template>
        </div>
      </div>

      <div v-if="filteredProducts?.length">
        <h3
          class="mb-2 text-sm font-semibold uppercase text-background-fg/70"
        >
          Products
        </h3>
        <div
          :class="[
            'grid',
            sparseModeEnabled
              ? 'grid-cols-2 gap-2'
              : 'grid-cols-3 gap-1',
          ]"
        >
          <template
            v-for="product in filteredProducts"
            :key="product.id"
          >
            <FullTillGridItemProduct
              v-on-long-press="[
                () => handleLongClickProduct(product),
                { delay: 500 },
              ]"
              :product="product"
              @click="handleClickProduct(product)"
            />
          </template>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex items-center justify-around p-4"
    >
      <p class="text-background-fg/87">
        No products or categories to display
      </p>
    </div>
  </div>

  <FullTillVariablePriceModal
    v-model:is-open="showVariablePriceModal"
    :product="currentProduct"
    @ok="handleSubmitVariablePriceModal"
  />

  <ProductInfoModal
    v-model:is-open="showProductInfoModal"
    :product="currentProduct"
  />

  <OrderItemDrawer ref="orderItemDrawerRef" />
</template>

<script setup lang="ts">
import type { Product } from "@/services/sharedTypes/products"
import type { Category } from "@/services/sharedTypes/categories"
import type { VariablePriceModalPayload } from "./FullTillVariablePriceModal.vue"
import { type DeepReadonly } from "vue"
import { vOnLongPress } from "@vueuse/components"
import { useProducts } from "@/services/products/listProducts"
import { useCategories } from "@/services/categories/listCategories"

const { sparseModeEnabled } = useSparseMode()
const router = useRouter()
const { addItem } = useCurrentOrder()
const props = withDefaults(
  defineProps<{ parentCategoryId?: string | null }>(),
  { parentCategoryId: null }
)

const searchString = ref<string>("")

const {
  data: categories,
  allCategories,
  getBreadcrumbs,
  isError: isCategoriesError,
  isLoading: isCategoriesLoading,
} = useCategories(props.parentCategoryId)
const {
  data: products,
  isError: isProductsError,
  isLoading: isProductsLoading,
} = useProducts(props.parentCategoryId)

const filteredCategories = computed(() => {
  if (!searchString.value) return categories.value
  const searchLower = searchString.value.toLowerCase()
  return (
    allCategories.value?.filter(
      (category) =>
        category.name.toLowerCase().includes(searchLower) ||
        category.namePath
          .toLowerCase()
          .includes(searchLower)
    ) ?? []
  )
})

const filteredProducts = computed(() => {
  if (!searchString.value) return products.value
  const searchLower = searchString.value.toLowerCase()
  return products.value?.filter((product) =>
    product.name.toLowerCase().includes(searchLower)
  )
})

const categoryPath = getBreadcrumbs(props.parentCategoryId)

const { breadcrumbs } = useBreadcrumbs()

watch(
  () => categoryPath.value,
  (value) => {
    breadcrumbs.value = value
  },
  {
    deep: true,
    immediate: true,
  }
)
const handleClickCategory = (
  category: DeepReadonly<Category>
) => {
  router.push({
    params: {
      parentCategoryId: category.id,
    },
  })
}

const orderItemDrawerRef = useTemplateRef(
  "orderItemDrawerRef"
)

const showVariablePriceModal = ref(false)
const currentProduct = ref<Product | null>(null)
const handleClickProduct = async (product: Product) => {
  if (product.hasVariablePrice) {
    currentProduct.value = product
    showVariablePriceModal.value = true
  } else if (product.modifierGroupIds.length > 0) {
    if (!orderItemDrawerRef.value) return

    const { isCanceled, data: modifierPayload } =
      await orderItemDrawerRef.value.reveal({
        product,
      })
    if (isCanceled || !modifierPayload) return
    addItem({
      ...modifierPayload.params,
      product: product,
      price: product.price,
    })
  } else {
    addItem({
      product: product,
      note: null,
      price: product.price,
      modifiers: null,
      quantity: 1,
    })
  }
}

const handleSubmitVariablePriceModal = ({
  product,
  price,
}: VariablePriceModalPayload) => {
  if (price && product) {
    addItem({
      product: product,
      price: price,
      note: null,
      modifiers: null,
      quantity: 1,
    })
  }
  showVariablePriceModal.value = false
}

const showProductInfoModal = ref(false)
const handleLongClickProduct = (product: Product) => {
  currentProduct.value = product
  showProductInfoModal.value = true
}
</script>
